import { useCallback, useEffect, useState } from "react";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import { Box, IconButton } from "@mui/material";
import TaskItem from "./components/TaskItem";
import AddIcon from '@mui/icons-material/Add';
import AddTaskForm from "../../../../../components/forms/tasks/AddTaskForm";
import { useModal } from "../../../../../hooks/useModal";
import LoadingAnimation from "../../../../../components/_common/Loading";
import tasksController from "../../../../../Api/tasksController";

const initialColumns = {
    ideas: {
        name: "Pomysły",
        type: "ideas",
        color: "#fffde7",
    },
    todo: {
        name: "Do zrobienia",
        color: "#ffebee",
        type: "todo",
    },
    inprogress: {
        name: "W trakcie",
        color: "#e3f2fd",
        type: "inprogress",
    },
    done: {
        name: "Zrobione",
        color: "#e8f5e9",
        type: "done",
    },
};

export default function TasksComponent() {
    const [loading, setLoading] = useState(true);
    const [tasks, setTasks] = useState([]);
    const modal = useModal();

    // Tworzymy kolumny na podstawie tasków
    const columns = Object.entries(initialColumns).reduce((acc, [key, col]) => {
        acc[key] = {
            ...col,
            items: tasks
                .filter(t => t.column === key)
                .sort((a, b) => a.order - b.order),
        };
        return acc;
    }, {});

    const getTasks = useCallback(async () => {
        setLoading(true);
        const res = await tasksController.getAllTasks();
        if (res.ok) {
            setTasks(res.tasks || []);
        } else {
            console.error("Błąd pobierania zadań:", res.error);
        }
        setLoading(false);
    }, []);

    const onDragEnd = async (result) => {
        if (!result.destination) return;

        const { source, destination } = result;
        const sourceCol = source.droppableId;
        const destCol = destination.droppableId;

        // Kopiujemy taski z odpowiednich kolumn
        const sourceItems = [...columns[sourceCol].items];
        const destItems = sourceCol === destCol ? sourceItems : [...columns[destCol].items];

        const [removed] = sourceItems.splice(source.index, 1);
        destItems.splice(destination.index, 0, removed);

        // Aktualizujemy kolumny
        let updatedTasks = [...tasks].map(task => {
            if (task._id === removed._id) {
                return {
                    ...task,
                    column: destCol,
                    order: destination.index,
                };
            }
            if (task.column === sourceCol && sourceCol === destCol) {
                // Sortowanie w tej samej kolumnie
                const idx = sourceItems.findIndex(i => i._id === task._id);
                return idx !== -1 ? { ...task, order: idx } : task;
            }
            if (task.column === sourceCol && sourceCol !== destCol) {
                // Usunięcie z oryginalnej kolumny
                const idx = sourceItems.findIndex(i => i._id === task._id);
                return idx !== -1 ? { ...task, order: idx } : task;
            }
            if (task.column === destCol && sourceCol !== destCol) {
                // Dodanie do nowej kolumny
                const idx = destItems.findIndex(i => i._id === task._id);
                return idx !== -1 ? { ...task, order: idx } : task;
            }
            return task;
        });

        setTasks(updatedTasks);

        // Wyślij do API i obsłuż błąd
        const res = await tasksController.updateManyTasks(updatedTasks);
        if (!res.ok) {
            if (typeof getTasks === "function") {
                getTasks();
            }
        }
    };

    useEffect(() => {
        getTasks();
    }, [getTasks]);

    if (loading) return <LoadingAnimation />;

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <div style={{ display: "flex", gap: 32, justifyContent: "center", marginTop: 40, flexWrap: "wrap" }}>
                {Object.entries(columns).map(([columnId, column]) => (
                    <Droppable droppableId={columnId} key={columnId}>
                        {(provided, snapshot) => (
                            <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                style={{
                                    background: column.color,
                                    borderRadius: 16,
                                    width: 260,
                                    minWidth: 260,
                                    maxWidth: 260,
                                    minHeight: 320,
                                    padding: 30,
                                    boxShadow: snapshot.isDraggingOver
                                        ? "0 4px 24px #1976d233"
                                        : "0 2px 12px #0001",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    transition: "box-shadow 0.2s",
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                    <h3 style={{ marginTop: 0, marginBottom: 16 }}>{column.name}</h3>
                                    <IconButton>
                                        <AddIcon
                                            onClick={() => modal.open(
                                                <AddTaskForm
                                                    taskType={column.type}
                                                    columnName={column.name}
                                                    onRefreshTasks={getTasks}
                                                />
                                            )}
                                        />
                                    </IconButton>
                                </Box>
                                {column.items.map((item, idx) => (
                                    <TaskItem item={item} idx={idx} key={item._id} onRefreshTasks={getTasks} />
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                ))}
            </div>
        </DragDropContext>
    );
}